import { FC, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import * as React from 'react';

import { useAmplitude } from '@crehana/analytics';
import { Container } from '@crehana/ui/dist/GridV2';

import { AMPLITUDE_TRACKS } from '@/views/B2BLanding/events';

import Logo from '../Logo';
import Backdrop from './components/Backdrop';
import ConfirmationDialog from './components/ConfirmationDialog';
import ContactDialog from './components/ContactDialog';
import DropdownMenu from './components/DropdownMenu';
import Right from './components/Right';
import { DATA } from './Header.data';
import { MENU_OPTIONS } from './Header.models';
import { HeaderWrapper } from './Header.styles';

interface HeaderProps {
  darkMode?: boolean;
  transparent?: boolean;
  position?: 'sticky' | 'absolute';
  bgColor?: string;
  showCallButton?: boolean;
  openTrialForm?: () => void;
}

const Header: FC<React.PropsWithChildren<HeaderProps>> = ({
  darkMode = false,
  position = 'absolute',
  bgColor = 'bg-white',
  showCallButton,
  openTrialForm,
  transparent: isWhite,
}) => {
  const { logEvent } = useAmplitude();
  const node: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [isMobileOpen, setIsMobileOpen] = useState<boolean>(false);
  const [itemOpen, setItemOpen] = useState<MENU_OPTIONS | null>(null);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [openContactDialog, setOpenContactDialog] = useState<boolean>(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] =
    useState<boolean>(false);
  const isMenuOpen = itemOpen || isMobileOpen;
  const isDarkMode = !isSticky && !isMenuOpen && darkMode;
  const toogleBackground = () => {
    if (isMenuOpen) return 'bg-white';
    return isWhite ? 'bg-transparent' : bgColor;
  };

  const items = useMemo(() => {
    const objectItems = Object.keys(DATA);

    return objectItems.map(key => ({
      ...DATA[key as keyof typeof MENU_OPTIONS],
      key,
    }));
  }, [DATA, MENU_OPTIONS]);

  const HEADER_CLASSES = {
    sticky: `fixed ${
      isSticky || isMenuOpen ? 'bg-white shadow-dropdown' : 'bg-transparent'
    }`,
    absolute: `${toogleBackground()}`,
  };

  useEffect(() => {
    if (isMobileOpen) {
      logEvent({
        event: AMPLITUDE_TRACKS.MENU_OPENED,
        data: {},
      });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isMobileOpen, logEvent]);

  useEffect(() => {
    if (position === 'sticky') {
      const observer = new IntersectionObserver(
        ([e]) =>
          e.intersectionRatio < 1 ? setIsSticky(true) : setIsSticky(false),
        { threshold: [1] },
      );

      if (node.current) {
        observer.observe(node.current);
      }

      return () => {
        if (node.current) {
          observer.unobserve(node.current);
        }
      };
    }
  }, [position]);

  const onDismiss = () => {
    setItemOpen(null);
    setIsMobileOpen(false);
  };

  const toggleItem = (key: MENU_OPTIONS) => {
    if (itemOpen === key) {
      setItemOpen(null);
    } else {
      logEvent({
        event: AMPLITUDE_TRACKS.MENU_OPTION_CLICKED,
        data: {
          Section: DATA[key].name,
        },
      });
      setItemOpen(key);
    }
  };

  const toggleOpenMobile = () => {
    if (isMobileOpen) {
      onDismiss();
    } else {
      setIsMobileOpen(true);
    }
  };

  const closeDropdownMenu = () => {
    setItemOpen(null);
    setIsMobileOpen(false);
  };

  return (
    <header>
      <div className={`${position} top-0 left-0 right-0 z-dialog`} ref={node}>
        <HeaderWrapper
          className={`w-full ${HEADER_CLASSES[position]} ${
            isMobileOpen ? 'h-screen sm:h-auto' : ''
          } overflow-auto`}
          isMenuOpen={!!isMenuOpen}
        >
          <Container className="flex justify-between items-center py-16">
            <div className="w-24 lg:hidden" />
            <Logo darkMode={isDarkMode || (!!isWhite && !itemOpen)} />
            <Right
              darkMode={isDarkMode || (!!isWhite && !itemOpen)}
              itemOpen={itemOpen}
              toggleItem={toggleItem}
              items={items}
              showCallButton={showCallButton}
              toggleOpenMobile={toggleOpenMobile}
              isMobileOpen={isMobileOpen}
              setOpenContactDialog={setOpenContactDialog}
              openTrialForm={openTrialForm}
              setOpenConfirmationDialog={setOpenConfirmationDialog}
            />
          </Container>
          <DropdownMenu
            isDesktopOpen={!!itemOpen}
            isMobileOpen={isMobileOpen}
            itemData={itemOpen && DATA[itemOpen].data}
            items={items}
            itemOpen={itemOpen}
            toggleItem={toggleItem}
            showCallButton={showCallButton}
            setOpenContactDialog={setOpenContactDialog}
            closeDropdownMenu={closeDropdownMenu}
            openTrialForm={openTrialForm}
          />
          {isMenuOpen && <Backdrop onDismiss={onDismiss} />}
          <ContactDialog
            open={openContactDialog}
            onDismiss={() => {
              setOpenContactDialog(false);
            }}
          />
          <ConfirmationDialog
            open={openConfirmationDialog}
            onDismiss={() => {
              setOpenConfirmationDialog(false);
            }}
          />
        </HeaderWrapper>
      </div>
    </header>
  );
};

export default Header;
